import { useTranslation } from "react-i18next";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

export default function Hero() {
  const { t, i18n } = useTranslation();
  const sectionRef = useRef<HTMLElement | null>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const imageY = useTransform(scrollYProgress, [0, 1], ["-4%", "10%"]);
  const isRTL = i18n.language?.startsWith("ar");
  const whatsappMessage = isRTL
    ? "مرحباً، أرغب في طلب عرض سعر من ابراج الرفوف."
    : "Hello, I would like to request a quote from Arafouf.";
  const whatsappLink = `https://wa.me/966532785554?text=${encodeURIComponent(whatsappMessage)}`;
  const headline = t("hero.headline");
  const animatedPhrase = isRTL ? "من الألف إلى الياء" : "from A to Z";

  const renderAnimatedHeadline = () => {
    if (!headline.includes(animatedPhrase)) {
      return headline;
    }

    const [before, after] = headline.split(animatedPhrase);

    return (
      <>
        {before}
        <span className="hero-headline-loop-fill">{animatedPhrase}</span>
        {after}
      </>
    );
  };

  return (
    <section
      ref={sectionRef}
      id="hero"
      className="relative w-full min-h-[600px] md:min-h-[800px] flex items-end overflow-hidden"
    >
      <div className="absolute inset-0">
        <motion.img
          src="/assets/images/16.jpg"
          alt="Warehouse racking systems"
          className="w-full h-[112%] object-cover object-top"
          style={{ y: imageY }}
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.5, ease: "easeOut" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20" />
      </div>

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 pb-16 md:pb-24 pt-40 md:pt-60">
        <div className="max-w-4xl">
          <motion.h1
            className="text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-[1.25] md:leading-[1.2] lg:leading-[1.15] mb-6"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
          >
            {renderAnimatedHeadline()}
          </motion.h1>
          <motion.p
            className="text-xl md:text-2xl text-white/80 max-w-2xl mb-8 leading-relaxed"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
          >
            {t("hero.subheadline")}
          </motion.p>
          <div className="flex flex-col items-center md:items-start gap-4 md:gap-0">
            <motion.a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-brand text-white text-lg font-semibold px-8 py-4 rounded-full hover:bg-brand-dark transition-colors whitespace-nowrap cursor-pointer"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {t("hero.cta")}
              <span className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-full">
                <i className="ri-whatsapp-line text-white text-sm" />
              </span>
            </motion.a>

            <motion.a
              href="#solutions-showcase"
              className="md:hidden mt-1 flex h-11 w-11 items-center justify-center rounded-full border border-white/35 bg-white/10 text-white/90 backdrop-blur-sm hover:text-white transition-colors"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1 }}
              aria-label={t("hero.scrollHint")}
            >
              <motion.i
                className="ri-arrow-down-line text-xl"
                animate={{ y: [0, 5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              />
            </motion.a>
          </div>
        </div>
      </div>

      <motion.a
        href="#solutions-showcase"
        className="hidden md:inline-flex absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex-col items-center gap-2 text-white/85 hover:text-white transition-colors"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 1 }}
        aria-label={t("hero.scrollHint")}
      >
        <span className="text-xs md:text-sm font-medium tracking-wide">{t("hero.scrollHint")}</span>
        <motion.span
          className="w-10 h-10 rounded-full border border-white/35 bg-white/10 backdrop-blur-sm inline-flex items-center justify-center"
          animate={{ y: [0, 7, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
        >
          <i className="ri-arrow-down-line text-lg" />
        </motion.span>
      </motion.a>
    </section>
  );
}